// Utility functions for performance optimization
export const lazyLoadImage = (imageSrc: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = imageSrc;
    img.onload = () => resolve(img);
    img.onerror = reject;
  });
};

export const debounce = (func: Function, wait: number) => {
  let timeout: NodeJS.Timeout;
  return function executedFunction(...args: any[]) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

export const throttle = (func: Function, limit: number) => {
  let inThrottle: boolean;
  return function executedFunction(...args: any[]) {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
};

export const measurePerformance = (name: string, fn: Function) => {
  const start = performance.now();
  fn();
  const end = performance.now();
  console.log(`${name} took ${end - start}ms`);
};

export const preloadImage = (src: string) => {
  const link = document.createElement('link');
  link.rel = 'preload';
  link.as = 'image';
  link.href = src;
  document.head.appendChild(link);
};

export const preloadCriticalAssets = (assets: string[]) => {
  assets.forEach(preloadImage);
};

// Device capability detection helpers
export const detectDeviceCapabilities = () => {
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  const isOlderDevice = typeof navigator.deviceMemory !== 'undefined' ? 
    navigator.deviceMemory < 4 : false;
    
  const isLowPerformance = isMobile && isOlderDevice;
  const isMediumPerformance = isMobile && !isOlderDevice;
  const isHighPerformance = !isMobile;
  
  // Detect GPU capabilities if possible
  const hasGoodGPU = () => {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    
    if (!gl) return false;
    
    const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
    if (!debugInfo) return false;
    
    const renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
    
    // Check for keywords suggesting high-end GPUs
    const highEndGPUPatterns = [
      /nvidia/i, /geforce/i, /quadro/i, 
      /amd/i, /radeon/i, 
      /intel iris/i, /intel hd 5/i, /intel hd 6/i, /intel uhd/i,
      /apple m1/i, /apple m2/i, /apple gpu/i
    ];
    
    return highEndGPUPatterns.some(pattern => pattern.test(renderer));
  };
  
  return {
    isMobile,
    isOlderDevice,
    isLowPerformance,
    isMediumPerformance,
    isHighPerformance,
    hasGoodGPU: hasGoodGPU(),
    preferredQuality: isLowPerformance ? 'low' : (isMediumPerformance ? 'medium' : 'high')
  };
};

// Frame rate monitoring for adaptive quality
export class PerformanceMonitor {
  private fpsBuffer: number[] = [];
  private fpsBufferSize: number = 10;
  private lastFrameTime: number = 0;
  private frameCount: number = 0;
  private lastFPSUpdate: number = 0;
  private fpsUpdateInterval: number = 1000; // 1 second
  private onFPSUpdate: (fps: number, averageFps: number) => void;
  private animationFrameId: number | null = null;
  
  constructor(onFPSUpdate: (fps: number, averageFps: number) => void) {
    this.onFPSUpdate = onFPSUpdate;
  }
  
  public start(): void {
    if (this.animationFrameId) return;
    this.lastFrameTime = performance.now();
    this.frameCount = 0;
    this.lastFPSUpdate = this.lastFrameTime;
    this.animationFrameId = requestAnimationFrame(this.update);
  }
  
  public stop(): void {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }
  
  private update = (timestamp: number): void => {
    // Count frames
    this.frameCount++;
    
    // Check if we should calculate FPS
    if (timestamp - this.lastFPSUpdate >= this.fpsUpdateInterval) {
      const fps = Math.round((this.frameCount * 1000) / (timestamp - this.lastFPSUpdate));
      
      // Add to buffer and maintain buffer size
      this.fpsBuffer.push(fps);
      if (this.fpsBuffer.length > this.fpsBufferSize) {
        this.fpsBuffer.shift();
      }
      
      // Calculate average FPS
      const averageFps = this.fpsBuffer.reduce((sum, value) => sum + value, 0) / this.fpsBuffer.length;
      
      // Callback with current values
      this.onFPSUpdate(fps, averageFps);
      
      // Reset counters
      this.lastFPSUpdate = timestamp;
      this.frameCount = 0;
    }
    
    this.lastFrameTime = timestamp;
    this.animationFrameId = requestAnimationFrame(this.update);
  };
}